import { colors } from "healthex-common/colors";
import { fontSizes } from "healthex-common/sizes";
import { fontWeights } from "healthex-common/weights";
import styled from "styled-components";
import { multilineEllipsis } from "./base.styles";
import { FlexRow } from "./common.styles";
import { SourceStatus } from "./PatientSources.types";
export const SourceCardRoot = styled.div `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    border-radius: 32px;
    background-color: ${(props) => (props.$muted ? colors.insight_card_bg : colors.history_timeline_card_bg)};
    border: ${(props) => (props.$muted ? `0.47px dashed ${colors.separator_main}` : `0.57px solid ${colors.separator_main}`)};
    transition: border-color 120ms ease;

    /* Renders as a <button> when clickable (connected cards) - reset the browser's default button
       chrome so it reads the same as the plain <div> muted cards use. */
    ${(props) => props.$clickable &&
    `
        padding: 0;
        margin: 0;
        font: inherit;
        text-align: inherit;
        color: inherit;
        cursor: pointer;

        /* Same technique as StatCard/PhysiciansCard on the Source Details page - swap border-color
           only, never border-width. A box-shadow-ring overlay was tried here too, but a plain
           color-only border avoids two problems at once: no width change means no layout shift
           (the card is auto-height, so a wider border pushes every card below it down a pixel or
           two), and a real border paints at the element's own outer edge, so - unlike a shadow -
           it's never hidden behind SourceCardTop's opaque white header background. */
        @media (hover: hover) {
            &:hover {
                border-color: ${colors.navy_50_opaque};
            }
        }

        &:focus-visible {
            border-color: ${colors.navy_50_opaque};
        }
    `}
`;
export const SourceCardTop = styled(FlexRow) `
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
    background-color: ${(props) => (props.$muted ? "transparent" : colors.white_main)};
`;
export const SourceAvatar = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 54px;
    height: 54px;
    border-radius: 1000px;
    overflow: hidden;
    background-color: ${(props) => (props.$hasLogo ? "transparent" : colors.navy_light)};
`;
export const SourceAvatarImage = styled.img `
    width: 100%;
    height: 100%;
    object-fit: contain;
`;
export const SourceAvatarInitial = styled.span `
    font-size: 20px;
    font-weight: ${fontWeights.BOLD};
    color: ${colors.white_main};
`;
export const SourceHeadline = styled.div `
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
    align-self: stretch;

    /* Targets HXTooltipWrapperView's own rendered span (SourceName/SourceAddress are each wrapped
       in one) - that span has no min-width/overflow of its own, so despite its stretch prop
       correctly matching this column's width, it never shrinks below the label's full un-clamped
       content width, leaving multilineEllipsis nothing bounded to actually clip against. Same fix
       PatientSourceDetailsView.styles.ts's RecordRowLabelContainer uses for the same reason. */
    > span {
        min-width: 0;
        overflow: hidden;
    }
`;
export const SourceName = styled.p `
    ${multilineEllipsis(1)}
    margin: 0;
    font-size: ${fontSizes.STANDARD};
    font-weight: ${fontWeights.BOLD};
    color: ${colors.black_main};
    letter-spacing: -0.31px;
    line-height: 21px;
`;
export const SourceAddress = styled.p `
    ${multilineEllipsis(1)}
    margin: 0;
    font-size: ${fontSizes.SMALL};
    font-weight: ${fontWeights.NORMAL};
    color: ${colors.black_lighter};
    line-height: 21.5px;
    letter-spacing: -0.15px;
`;
export const SourceChevronWrapper = styled.div `
    display: flex;
    align-items: center;
    height: 48px;
    flex-shrink: 0;
    /* Verified against the Figma "Discloser Indicator" vector fill (#797E85) directly, not
       black_main - the chevron renders noticeably lighter than the card's body text. */
    color: ${colors.history_secondary_text};
`;
export const SourceAdditionalDetails = styled.div `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
`;
export const SourceStatsRow = styled.div `
    display: flex;
    gap: 4px;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    padding: 16px 20.5px 8px;
    text-align: center;
    border-top: 0.37px solid ${colors.separator_main};
`;
export const SourceStatColumn = styled.div `
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
    align-items: center;
`;
export const SourceStatValue = styled.p `
    margin: 0;
    font-size: 14px;
    font-weight: ${fontWeights.BOLD};
    letter-spacing: -0.15px;
    line-height: 21.5px;
    color: ${colors.black_light};
`;
export const SourceStatLabel = styled.p `
    margin: 0;
    font-size: 12px;
    font-weight: ${fontWeights.NORMAL};
    line-height: 18px;
    color: ${colors.black_lighter};
`;
export const SourceStatusRow = styled(FlexRow) `
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    padding: 16px 0;
    border-top: 0.37px solid ${colors.separator_main};
`;
function statusBadgeAccent(status) {
    return status === SourceStatus.CONNECTED ? "#3f9c19" : "#5b616a";
}
function statusBadgeBackground(status) {
    return status === SourceStatus.CONNECTED ? "rgba(63, 156, 25, 0.1)" : "rgba(91, 97, 106, 0.2)";
}
export const SourceStatusBadge = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 24px;
    box-sizing: border-box;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid ${(props) => statusBadgeAccent(props.$status)};
    background-color: ${(props) => statusBadgeBackground(props.$status)};
    color: ${(props) => statusBadgeAccent(props.$status)};
    font-size: 12px;
    font-weight: ${fontWeights.SEMI_BOLD};
    letter-spacing: 0.14px;
    text-transform: uppercase;
    white-space: nowrap;
`;
export const SourceRefreshedText = styled.p `
    margin: 0;
    display: flex;
    gap: 4px;
    font-size: 10px;
    font-weight: ${fontWeights.NORMAL};
    letter-spacing: 0.12px;
    line-height: 14px;
    color: ${(props) => (props.$muted ? colors.black_30 : colors.black_lighter)};
    white-space: nowrap;
`;
